import { Building04 } from "@untitledui/icons";
import type { Key, ReactElement, ReactNode } from "react";
import {
	DisplayInput,
	DisplaySelect,
	DisplaySelectBadgeVariant,
	DisplaySelectEmptyTrailing,
	DisplaySelectValueType,
	DisplayTextarea,
} from "@/components/Atoms/DisplayField";
import { DisplayFieldState } from "@/components/Atoms/DisplayField/display-field.shared";
import {
	Tab,
	TabList,
	TabPanel,
	Tabs,
} from "@/components/application/tabs/tabs";
import { EmptyState } from "@/components/Molecules/EmptyState/EmptyState";
import {
	ListItem,
	ListItemState,
	ListItemType,
} from "@/components/Molecules/ListItem/ListItem";
import {
	type AppTranslateFn,
	useAppTranslation,
} from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

/** Figma Details loading — 7 × 54px skeleton rows (4767:56139). */
const DETAILS_LOADING_KEYS = [
	"details-skeleton-a",
	"details-skeleton-b",
	"details-skeleton-c",
	"details-skeleton-d",
	"details-skeleton-e",
	"details-skeleton-f",
	"details-skeleton-g",
] as const;
/** Figma Activity loading — avatar + card rows (4769:24985). */
const ACTIVITY_LOADING_KEYS = [
	"activity-skeleton-a",
	"activity-skeleton-b",
	"activity-skeleton-c",
	"activity-skeleton-d",
] as const;
/** Figma Assignees loading — ListItem card skeletons (4771:18229). */
const ASSIGNEE_LOADING_KEYS = [
	"assignee-skeleton-a",
	"assignee-skeleton-b",
] as const;

export const DetailsEntity = {
	Job: "job",
	Ticket: "ticket",
	Asset: "asset",
	Record: "record",
} as const;

export type DetailsEntity = (typeof DetailsEntity)[keyof typeof DetailsEntity];

export const DetailsState = {
	Default: "default",
	Empty: "empty",
	Loading: "loading",
} as const;

export type DetailsState = (typeof DetailsState)[keyof typeof DetailsState];

export const DetailsTab = {
	Details: "details",
	Activity: "activity",
	Assignee: "assignee",
	Attachments: "attachments",
} as const;

export type DetailsTab = (typeof DetailsTab)[keyof typeof DetailsTab];

export const DetailsSurface = {
	Primary: "primary",
	Tertiary: "tertiary",
} as const;

export type DetailsSurface =
	(typeof DetailsSurface)[keyof typeof DetailsSurface];

export interface DetailsTabItem {
	id: DetailsTab | string;
	label: string;
}

export interface DetailsProps {
	/**
	 * Entity type — drives default tabs and demo field layouts.
	 * @default "job"
	 */
	entity?: DetailsEntity;
	/**
	 * Content state for the details form.
	 * @default "default"
	 */
	state?: DetailsState;
	/**
	 * Tab bar chrome — `primary` for drawer, `tertiary` for sidebar.
	 * @default "primary"
	 */
	surface?: DetailsSurface;
	/**
	 * Active tab when uncontrolled.
	 * @default "details"
	 */
	defaultTab?: DetailsTab | string;
	/** Controlled selected tab id. */
	selectedTabId?: DetailsTab | string;
	/** Called when the selected tab changes. */
	onTabChange?: (tabId: string) => void;
	/**
	 * Override tab list. Defaults by entity:
	 * Job → Details / Assignees / Activity; others → Details / Activity.
	 */
	tabs?: DetailsTabItem[];
	/** Custom details-tab body. When omitted, entity demo fields render. */
	children?: ReactNode;
	/** Custom activity-tab body. */
	activityContent?: ReactNode;
	/** Custom assignees-tab body (Job only by default). */
	assigneeContent?: ReactNode;
	/** Custom attachments-tab body. */
	attachmentsContent?: ReactNode;
	/**
	 * When true, the Location row is empty (“Set location” + plus) even if
	 * `state` is default. Matches Figma set-location empty (46:3401).
	 */
	locationEmpty?: boolean;
	className?: string;
	"data-testid"?: string;
}

/**
 * Tabbed entity details panel for drawers (Figma Details 4767:55283).
 *
 * Compose with DisplayInput / DisplaySelect / DisplayTextarea in `children`
 * for production data; entity presets power Storybook and Code Connect demos.
 */
export function Details({
	entity = DetailsEntity.Job,
	state = DetailsState.Default,
	surface = DetailsSurface.Primary,
	defaultTab = DetailsTab.Details,
	selectedTabId,
	onTabChange,
	tabs: tabsProp,
	children,
	activityContent,
	assigneeContent,
	attachmentsContent,
	locationEmpty = false,
	className,
	"data-testid": dataTestId = "details",
}: DetailsProps): ReactElement {
	const { t } = useAppTranslation();
	const tabs = tabsProp ?? getDefaultTabs(entity, t);
	const resolvedDefault = defaultTab ?? tabs[0]?.id ?? DetailsTab.Details;
	const isLoading = state === DetailsState.Loading;
	const loadingDetailsLabel = t("common.a11y.loadingDetails");
	const loadingActivityLabel = t("common.a11y.loadingActivity");
	const loadingAssigneesLabel = t("common.a11y.loadingAssignees");

	return (
		<div
			className={cx(
				// Transparent shell so Drawer ScrollBody / Sidebar `bg-tertiary` shows
				"flex w-full flex-col items-start bg-transparent",
				className,
			)}
			data-testid={dataTestId}
			data-entity={entity}
			data-state={state}
			data-surface={surface}
		>
			<Tabs
				selectedKey={selectedTabId}
				defaultSelectedKey={selectedTabId == null ? resolvedDefault : undefined}
				onSelectionChange={(key: Key) => {
					onTabChange?.(String(key));
				}}
				className="w-full"
			>
				{/* Tab bar — Figma 4771:17708: h-48, px-3xl; underline pinned to bottom rule */}
				<div
					className={cx(
						"flex h-12 w-full shrink-0 flex-col items-stretch rounded-none border-b border-secondary px-(--spacing-3xl,24px)",
						surface === DetailsSurface.Tertiary
							? "bg-transparent"
							: "bg-primary",
					)}
				>
					<TabList
						type="underline"
						size="sm"
						items={tabs.map((tab) => ({
							id: tab.id,
							label: tab.label,
						}))}
						className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
					>
						{(item) => (
							<Tab
								id={item.id}
								label={item.label}
								className="h-full items-end -mb-px"
							/>
						)}
					</TabList>
				</div>

				{tabs.map((tab) => (
					<TabPanel key={tab.id} id={tab.id} className="w-full">
						{tab.id === DetailsTab.Details || tab.id === "details"
							? renderDetailsPanel({
									state,
									entity,
									children,
									loadingDetailsLabel,
									locationEmpty,
								})
							: null}
						{tab.id === DetailsTab.Activity || tab.id === "activity"
							? isLoading
								? renderActivityLoading(loadingActivityLabel)
								: (activityContent ?? (
										<EmptyState
											type="default"
											title="No Activity Yet"
											description="Updates and changes will appear here."
											showAction={false}
											className="min-h-[280px] max-w-none py-8"
										/>
									))
							: null}
						{tab.id === DetailsTab.Attachments || tab.id === "attachments"
							? attachmentsContent != null
								? (
									<div className="flex w-full flex-col p-(--spacing-xl,16px)">
										{attachmentsContent}
									</div>
								)
								: null
							: null}
						{tab.id === DetailsTab.Assignee || tab.id === "assignee"
							? isLoading
								? renderAssigneeLoading(loadingAssigneesLabel)
								: (assigneeContent ?? (
										<EmptyState
											type="default"
											title="No Assignees Yet"
											description="People assigned to this job will show up here."
											showAction={false}
											className="min-h-40 max-w-none py-8"
										/>
									))
							: null}
					</TabPanel>
				))}
			</Tabs>
		</div>
	);
}

/**
 * Details-tab field skeletons — Figma Loading / Active Tab=Details (4767:56139).
 */
function renderDetailsLoading(ariaLabel: string): ReactElement {
	return (
		<output
			className="flex w-full flex-col items-center gap-(--spacing-sm,6px) overflow-clip p-(--spacing-xl,16px)"
			data-testid="details-loading"
			aria-busy="true"
			aria-label={ariaLabel}
		>
			{DETAILS_LOADING_KEYS.map((key) => (
				<div
					key={key}
					className="h-[54px] w-full shrink-0 rounded-xs bg-tertiary"
				/>
			))}
		</output>
	);
}

/**
 * Activity-tab skeletons — Figma Loading / Active Tab=Activity (4769:24985).
 */
function renderActivityLoading(ariaLabel: string): ReactElement {
	return (
		<output
			className="flex h-[360px] w-full flex-col items-center gap-(--spacing-xs,4px) overflow-clip p-(--spacing-xl,16px)"
			data-testid="details-activity-loading"
			aria-busy="true"
			aria-label={ariaLabel}
		>
			{ACTIVITY_LOADING_KEYS.map((key) => (
				<div key={key} className="flex h-[88px] w-full items-start gap-1">
					<div className="size-8 shrink-0 rounded-full bg-tertiary" />
					<div className="h-full min-w-0 flex-1 rounded-xl bg-tertiary" />
				</div>
			))}
		</output>
	);
}

/**
 * Assignees-tab skeletons — Figma Loading / Active Tab=Assignee (4771:18229).
 */
function renderAssigneeLoading(ariaLabel: string): ReactElement {
	return (
		<output
			className="flex w-full flex-col items-start gap-(--spacing-xs,4px) p-(--spacing-xl,16px)"
			data-testid="details-assignee-loading"
			aria-busy="true"
			aria-label={ariaLabel}
		>
			{ASSIGNEE_LOADING_KEYS.map((key) => (
				<ListItem
					key={key}
					type={ListItemType.Card}
					state={ListItemState.Loading}
					className="w-full"
				/>
			))}
		</output>
	);
}

function getDefaultTabs(
	entity: DetailsEntity,
	t: AppTranslateFn,
): DetailsTabItem[] {
	if (entity === DetailsEntity.Job) {
		return [
			{
				id: DetailsTab.Details,
				label: t("main.jobs.drawer.tabs.details.label"),
			},
			{
				id: DetailsTab.Assignee,
				label: t("main.jobs.drawer.tabs.assignees.label"),
			},
			{
				id: DetailsTab.Activity,
				label: t("main.jobs.drawer.tabs.activity.label"),
			},
		];
	}

	if (entity === DetailsEntity.Ticket) {
		return [
			{
				id: DetailsTab.Details,
				label: t("main.tickets.drawer.tabs.details.label"),
			},
			{
				id: DetailsTab.Activity,
				label: t("main.tickets.drawer.tabs.activity.label"),
			},
		];
	}

	if (entity === DetailsEntity.Asset) {
		return [
			{
				id: DetailsTab.Details,
				label: t("main.tickets.workspace.assets.drawer.tabs.details.label"),
			},
			{
				id: DetailsTab.Activity,
				label: t("main.tickets.workspace.assets.drawer.tabs.activity.label"),
			},
		];
	}

	return [
		{
			id: DetailsTab.Details,
			label: t("main.tickets.workspace.records.drawer.tabs.details.label"),
		},
		{
			id: DetailsTab.Activity,
			label: t("main.tickets.workspace.records.drawer.tabs.activity.label"),
		},
	];
}

function renderDetailsPanel(options: {
	state: DetailsState;
	entity: DetailsEntity;
	children?: ReactNode;
	loadingDetailsLabel: string;
	locationEmpty: boolean;
}): ReactElement {
	const { state, entity, children, loadingDetailsLabel, locationEmpty } =
		options;

	if (state === DetailsState.Loading) {
		return renderDetailsLoading(loadingDetailsLabel);
	}

	return (
		<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
			{children ?? (
				<DefaultEntityFields
					entity={entity}
					state={state}
					locationEmpty={locationEmpty}
				/>
			)}
		</div>
	);
}

function DemoLocationField({ isEmpty }: { isEmpty: boolean }): ReactElement {
	return (
		<DisplaySelect
			label="Location"
			placeholder="Set location"
			value={isEmpty ? undefined : "Label"}
			valueType={DisplaySelectValueType.Label}
			valueIcon={Building04}
			state={DisplayFieldState.Enabled}
			showEdit={!isEmpty}
			onEdit={isEmpty ? undefined : () => undefined}
			emptyTrailing={DisplaySelectEmptyTrailing.Add}
			onEmptyAction={isEmpty ? () => undefined : undefined}
		/>
	);
}

/**
 * Demo / Storybook field stack matching Figma entity presets.
 */
function DefaultEntityFields({
	entity,
	state,
	locationEmpty,
}: {
	entity: DetailsEntity;
	state: DetailsState;
	locationEmpty: boolean;
}): ReactElement {
	const isEmpty = state === DetailsState.Empty;
	const isLocationEmpty = isEmpty || locationEmpty;
	const enabled = DisplayFieldState.Enabled;
	const disabled = DisplayFieldState.Disabled;

	if (entity === DetailsEntity.Ticket) {
		// Figma Ticket Details — Name → Priority → Due Date → Status → Assignee →
		// Type → Sub-Organization → ID → Location → Description
		return (
			<>
				<DisplayInput
					label="Name"
					value={isEmpty ? undefined : "Ticket Name"}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplaySelect
					label="Priority"
					placeholder="Select a priority"
					value={isEmpty ? undefined : "Badge"}
					valueType={DisplaySelectValueType.Badge}
					showValueIcon={false}
					badgeVariant={DisplaySelectBadgeVariant.Dot}
					badgeColor="gray"
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplayInput
					label="Due Date"
					value={isEmpty ? undefined : "mm/dd/yy"}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplaySelect
					label="Status"
					placeholder="Select a Status"
					value={isEmpty ? undefined : "Badge"}
					valueType={DisplaySelectValueType.Badge}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplaySelect
					label="Assignee"
					placeholder="Select an Assignee"
					value={isEmpty ? undefined : "Label"}
					valueType={DisplaySelectValueType.Text}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplaySelect
					label="Type"
					value={isEmpty ? undefined : "Badge"}
					valueType={DisplaySelectValueType.Badge}
					state={enabled}
					showEdit={!isEmpty}
					onEdit={isEmpty ? undefined : () => undefined}
				/>
				<DisplayInput
					label="Sub-Organization"
					value={isEmpty ? undefined : "Name"}
					state={isEmpty ? enabled : disabled}
					showEdit={false}
				/>
				<DisplayInput
					label="ID"
					value={isEmpty ? undefined : "1234...1234"}
					state={enabled}
					copyable={!isEmpty}
					showEdit={false}
				/>
				<DemoLocationField isEmpty={isLocationEmpty} />
				<DisplayTextarea
					label="Description"
					value={isEmpty ? undefined : "This is the text that is typed."}
					state={enabled}
					onEdit={() => undefined}
				/>
			</>
		);
	}

	if (entity === DetailsEntity.Asset || entity === DetailsEntity.Record) {
		const name = entity === DetailsEntity.Asset ? "Asset Name" : "Record Name";
		return (
			<>
				<DisplayInput
					label="Name"
					value={isEmpty ? undefined : name}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplaySelect
					label="Type"
					value={isEmpty ? undefined : "Label"}
					valueType={DisplaySelectValueType.Label}
					valueIcon={Building04}
					state={isEmpty ? disabled : enabled}
					showEdit={false}
				/>
				<DisplaySelect
					label="Status"
					placeholder="Select a status"
					value={isEmpty ? undefined : "Badge"}
					valueType={DisplaySelectValueType.Badge}
					state={enabled}
					onEdit={() => undefined}
				/>
				<DisplayInput
					label="Sub-Organization"
					value={isEmpty ? undefined : "Name"}
					state={isEmpty ? enabled : disabled}
					showEdit={isEmpty}
				/>
				<DisplayInput
					label="ID"
					value={isEmpty ? undefined : "1234...1234"}
					state={enabled}
					copyable={!isEmpty}
					showEdit={isEmpty}
				/>
				<DemoLocationField isEmpty={isLocationEmpty} />
				<DisplayTextarea
					label="Description"
					value={isEmpty ? undefined : "This is the text that is typed."}
					state={enabled}
					onEdit={() => undefined}
				/>
			</>
		);
	}

	// Job (default — matches Figma 4771:17708)
	return (
		<>
			<DisplayInput
				label="Name"
				value={isEmpty ? undefined : "Job Name"}
				state={enabled}
				onEdit={() => undefined}
			/>
			<DisplaySelect
				label="Type"
				value={isEmpty ? undefined : "Label"}
				valueType={DisplaySelectValueType.Label}
				valueIcon={Building04}
				state={isEmpty ? disabled : enabled}
				showEdit={false}
			/>
			<DisplaySelect
				label="Status"
				placeholder="Select a status"
				value={isEmpty ? undefined : "Badge"}
				valueType={DisplaySelectValueType.Badge}
				valueIcon={Building04}
				state={enabled}
				onEdit={() => undefined}
			/>
			<DisplayInput
				label="Sub-Organization"
				value={isEmpty ? undefined : "Name"}
				state={isEmpty ? enabled : disabled}
				showEdit={isEmpty}
			/>
			<DisplayInput
				label="ID"
				value={isEmpty ? undefined : "1234...1234"}
				state={enabled}
				copyable={!isEmpty}
				showEdit={isEmpty}
			/>
			<DemoLocationField isEmpty={isLocationEmpty} />
			<DisplayTextarea
				label="Description"
				value={isEmpty ? undefined : "This is the text that is typed."}
				state={enabled}
				onEdit={() => undefined}
			/>
		</>
	);
}
